'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { useTranslations, useLocale } from 'next-intl'
import { Link, usePathname, useRouter } from '@/i18n/navigation'
import { useHashActive } from './useHashActive'

export default function Header() {
  const t = useTranslations('nav')
  const locale = useLocale()
  const pathname = usePathname()
  const router = useRouter()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const navLinks = [
    { name: t('projects'), href: '/#projects' },
    { name: t('partners'), href: '/#partners' },
    { name: t('about'), href: '/#about' },
    { name: t('consulting'), href: '/consulting' },
    { name: t('jobs'), href: '/jobs' },
    { name: t('blog'), href: '/blog' },
  ]

  const handleLanguageChange = async (newLocale: 'en' | 'cs') => {
    // If we're on a job detail page (/jobs/[slug]), redirect to jobs list
    // because the slug will be different in the new language
    if (pathname.startsWith('/jobs/') && pathname.split('/').length > 2) {
      router.push('/jobs', { locale: newLocale })
      return
    }

    // If we're on a blog post page (/blog/[slug]), try to find the localized slug
    if (pathname.startsWith('/blog/') && pathname.split('/').length > 2) {
      const currentSlug = pathname.split('/').pop()

      try {
        // Try to get the post's slug for the target locale
        const response = await fetch(
          // TODO THIS MUST BE REFACTORED WTH
          `/api/get-post-slug?slug=${currentSlug}&locale=${newLocale}&currentLocale=${locale}`,
        )

        if (response.ok) {
          const data = await response.json()
          if (data.slug) {
            router.push(`/blog/${data.slug}`, { locale: newLocale })
            return
          }
        }
      } catch (error) {
        console.warn('Failed to get localized slug:', error)
      }

      // Fallback to blog list if we can't find the localized slug
      router.push('/blog', { locale: newLocale })
      return
    }

    // For all other pages, use the current pathname
    router.push(pathname, { locale: newLocale })
  }

  const toggleMobileMenu = () => setIsMenuOpen(!isMenuOpen)
  const closeMobileMenu = () => setIsMenuOpen(false)

  // Avoid SSR/CSR mismatch by deferring hash checks until after hydration
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])

  // Track current hash via hook
  const { hash, setHash } = useHashActive()

  const isActiveLink = (href: string) => {
    if (href.startsWith('/#')) {
      if (!hydrated) return false
      const target = `#${href.split('#')[1] || ''}`
      return pathname === '/' && hash === target
    }
    return pathname === href || (pathname.startsWith(href) && href !== '/')
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/40 backdrop-blur-lg border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link href="/" className="flex items-center h-full group">
              <Image
                src="/images/logo.svg"
                alt="StrandForge"
                width={140}
                height={56}
                className="h-12 object-contain filter brightness-0 invert transition-all duration-300 group-hover:scale-105"
                priority
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => {
              const isHash = link.href.startsWith('/#')
              const handleClick = () => {
                if (!isHash) return
                const target = `#${link.href.split('#')[1] || ''}`
                setHash(target)
              }
              return (
                <Link
                  key={link.name}
                  href={link.href}
                  onClick={handleClick}
                  className={`text-sm font-semibold transition-all duration-300 relative group ${isActiveLink(link.href) ? 'text-white' : 'text-gray-400 hover:text-white'
                    }`}
                >
                  {link.name}
                  {isActiveLink(link.href) ? (
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-strandforge-purple rounded-full" />
                  ) : (
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-strandforge-purple scale-x-0 group-hover:scale-x-100 transition-transform duration-300 rounded-full" />
                  )}
                </Link>
              )
            })}

            {/* Desktop Language Switcher */}
            <div className="flex items-center ml-8 bg-white/5 rounded-full px-1 py-1 border border-white/10">
              <button
                onClick={() => handleLanguageChange('cs')}
                className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all duration-300 ${locale === 'cs' ? 'text-black bg-white' : 'text-gray-400 hover:text-white'
                  }`}
              >
                CZ
              </button>
              <div className="w-px h-4 bg-white/20 mx-1" />
              <button
                onClick={() => handleLanguageChange('en')}
                className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all duration-300 ${locale === 'en' ? 'text-black bg-white' : 'text-gray-400 hover:text-white'
                  }`}
              >
                EN
              </button>
            </div>
          </div>

          {/* Mobile and tablet menu button */}
          <div className="lg:hidden">
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={isMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile and tablet menu */}
      {isMenuOpen && (
        <div
          id="mobile-menu"
          className="lg:hidden bg-black/50 backdrop-blur-lg border-t border-white/10"
        >
          <div className="px-4 pt-4 pb-6 space-y-2">
            {navLinks.map((link) => {
              const isHash = link.href.startsWith('/#')
              const handleClick = () => {
                if (isHash) {
                  const target = `#${link.href.split('#')[1] || ''}`
                  setHash(target)
                }
                closeMobileMenu()
              }
              return (
                <Link
                  key={link.name}
                  href={link.href}
                  onClick={handleClick}
                  className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 ${isActiveLink(link.href)
                      ? 'text-white bg-strandforge-purple/20 border border-strandforge-purple/30'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                >
                  {link.name}
                </Link>
              )
            })}

            {/* Mobile Language Switcher */}
            <div className="pt-4 mt-4 border-t border-white/10">
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => {
                    handleLanguageChange('cs')
                    closeMobileMenu()
                  }}
                  className={`text-sm font-bold px-4 py-2 rounded-full transition-all duration-300 ${locale === 'cs'
                      ? 'text-black bg-white'
                      : 'text-gray-400 hover:text-white bg-white/5'
                    }`}
                >
                  CZ
                </button>
                <button
                  onClick={() => {
                    handleLanguageChange('en')
                    closeMobileMenu()
                  }}
                  className={`text-sm font-bold px-4 py-2 rounded-full transition-all duration-300 ${locale === 'en'
                      ? 'text-black bg-white'
                      : 'text-gray-400 hover:text-white bg-white/5'
                    }`}
                >
                  EN
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
